import { gsap } from "gsap";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { DynamicBackground } from "../components/DynamicBackground";
import { CancellationToken } from "async/CancellationToken";
import { DollarsResourcePackage } from "../../../common/resources/DollarsResourcePackage";

export class CongratulationsPopupView extends BaseScalableView<void> {

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly dollarsResources: DollarsResourcePackage;
    private fadeScreen: FadeScreen;
    private background: DynamicBackground;
    private headerText: Text;
    private descriptionText: Text;
    private additionalText: Text;
    private button: ButtonWithScaleAnimation;
    private isAddToMarketPlace: boolean

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        dollarsResources: DollarsResourcePackage,
        canvasScaler: ICanvasScaler,
        isAddToMarketPlace: boolean
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.dollarsResources = dollarsResources;
        this.isAddToMarketPlace = isAddToMarketPlace;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public start(): void { }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.9, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.background = new DynamicBackground(this.dollarsResources, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.background);
        this.headerText = new Text("CONGRATULATIONS", new HeaderTextStyle());
        this.headerText.anchor.x = 0.5;
        this.headerText.skew._x = -0.087;
        this.headerText.angle = -5;
        this.addChild(this.headerText);
        this.descriptionText = new Text("", new DescriptionTextStyle());
        this.descriptionText.anchor.x = 0.5;
        this.addChild(this.descriptionText);
        this.additionalText = new Text("MARKETPLACE", new AdditionalTextStyle());
        if (this.isAddToMarketPlace) {
            this.descriptionText.text = "YOUR BUILDING\nHAS BEEN SUCCESSFULLY\nUPLOADED TO THE";
        }
        else {
            this.descriptionText.text = "BUILDING PRICE\nUPDATED SUCCESSFULLY";
            this.additionalText.visible = false;
        }
        this.additionalText.anchor.set(0.5);
        this.addChild(this.additionalText);
        this.button = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_blue_png);
        this.button.onUp.add(this.hidePage, this);
        this.addChild(this.button);
        const buttonText = new Text("SEE BUILDING IN", new ButtonTextStyle());
        buttonText.position.set(31, 51);
        this.button.defaultView.addChild(buttonText);
        const buttonIcon = new Sprite(this.commonResources.assets_common_ui_vector_png);
        buttonIcon.position.set(351, 51);
        this.button.defaultView.addChild(buttonIcon);
    }

    private onCancellationRequested(): void {
        this.operationControls.complete();
        this.destroy();
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        this.background.playIdleAnimation();
    }

    private async hidePage(): Promise<void> {
        this.button.interactive = false;
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        animationTimeLine.kill();
        this.operationControls.complete();
    }

    protected layout(width: number, height: number): void {
        this.background.position.set(width * 0.5, height * 0.5);
        this.headerText.position.set(width * 0.5, 538.52);
        if (!this.isAddToMarketPlace) {
            this.descriptionText.position.set(414, 801.9);
        }
        else {
            this.descriptionText.position.set(414, 735.4);
        }
        this.additionalText.position.set(width * 0.5, this.descriptionText.y + this.descriptionText.height + this.additionalText.height * 0.5);
        this.button.position.set(199, 1085);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.button.onUp.remove(this.hidePage, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#86f1d1",
                "#40bce0"
            ],
            fillGradientType: 1,
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 112
        }));
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            lineHeight: 68.5
        }));
    }
}

class AdditionalTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#37d2c9",
                "#37d2c9"
            ],
            fillGradientType: 1,
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            dropShadow: true,
            dropShadowAngle: 0,
            dropShadowAlpha: 1,
            dropShadowBlur: 20,
            dropShadowColor: "#c5a3e1",
            dropShadowDistance: 5,
        }));
    }
}
